import { useTheme } from '@mui/material';
import { ShapeType } from 'components/Procedure/Header/SlideInteractionMenu/SlideAnnotationTools/options';
import { TodoOption } from 'interfaces/annotation';
import { CellRuleValue } from 'interfaces/cellRule';
import { dropRight, find, forEach, includes, isEmpty, join, last, size, split } from 'lodash';
import { useCallback } from 'react';
import {
  CLASS_ID_MARKER_NEG_SUFFIX,
  CLASS_ID_MARKER_POS_SUFFIX,
  CLASS_ID_MARKER_VALUE_SEPARATOR,
  getShapeTypeFromTodoClassName,
  getTaxonomyIdAndMarkersFromClassId,
} from 'services/annotations/helpers';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';

export const useGetClassToAnnotate = (todoTypeOptions: TodoOption[] = []) => {
  const theme = useTheme();
  const { data: taxonomies, isLoading: isLoadingTaxonomy, isError: isErrorTaxonomies } = useTaxonomy();

  const getTodoOptionFromTaxonomyId = useCallback(
    (taxonomyId: string, fallbackColor?: string): TodoOption => {
      // after adding the type to the taxonomy table we should take the type from there
      const inferredClassName: 'polygon' | 'point' =
        includes(taxonomyId, 'cell') && !includes(taxonomyId, 'segmentation') ? 'point' : 'polygon';
      const inferredShapeType = inferredClassName === 'point' ? ShapeType.Point : ShapeType.Polygon;

      // search the name in todoTypes to get the color and className
      const todoType = find(todoTypeOptions, (todoTypeOption) => todoTypeOption.name === taxonomyId);
      const taxonomy = taxonomies ? find(taxonomies, (curTaxonomy) => curTaxonomy.path === taxonomyId) : undefined;
      const todoTypeShapeType = getShapeTypeFromTodoClassName(todoType?.className);

      return {
        name: taxonomyId,
        color: taxonomy?.defaultColor ?? todoType?.color ?? fallbackColor ?? theme.palette.primary.main,
        className: todoType?.className ?? inferredClassName,
        shapeType: todoType?.shapeType ?? todoTypeShapeType ?? inferredShapeType,
        displayName: taxonomy?.name || taxonomyId,
      };
    },
    [todoTypeOptions, taxonomies, theme.palette.primary.main]
  );

  const getTodoOptionFromClassId = useCallback(
    (classId: string, fallbackColor?: string): TodoOption => {
      const { taxonomyId, markers } = getTaxonomyIdAndMarkersFromClassId(classId);
      const resolvedTaxonomyId: string = taxonomyId ?? classId;
      const baseOption = getTodoOptionFromTaxonomyId(resolvedTaxonomyId, fallbackColor);

      const markerPositivity: NonNullable<TodoOption['markerPositivity']> = {};
      forEach(markers, (markerPart) => {
        const markerParts = split(markerPart, CLASS_ID_MARKER_VALUE_SEPARATOR);
        if (size(markerParts) < 2) {
          return;
        }

        const suffix = last(markerParts);
        const markerId = join(dropRight(markerParts), CLASS_ID_MARKER_VALUE_SEPARATOR);

        if (!suffix) {
          return;
        }

        if (!markerId) {
          return;
        }

        if (suffix === CLASS_ID_MARKER_POS_SUFFIX) {
          markerPositivity[markerId] = CellRuleValue.MustBePositive;
        } else if (suffix === CLASS_ID_MARKER_NEG_SUFFIX) {
          markerPositivity[markerId] = CellRuleValue.MustBeNegative;
        }
      });

      return {
        ...baseOption,
        ...(isEmpty(markerPositivity) ? {} : { markerPositivity }),
      };
    },
    [getTodoOptionFromTaxonomyId]
  );

  return {
    getTodoOptionFromTaxonomyId,
    getTodoOptionFromClassId,
    taxonomies,
    isLoadingTaxonomy,
    isErrorTaxonomies,
  };
};
